import type { GoalModalProps, GoalProps } from './modules/AddEditGoalModal';
import type { AxiosError } from 'axios';
import React, { useEffect, useRef } from 'react';
import AdminDashboardLayout from '../../../layouts/AdminDashboardLayout';
import { useI18n } from '@whizz/react-i18n';
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { ButtonIcon, MaterialIcon, Textarea } from '../../../components';
import { ADMIN_ROUTES, QUERIES_IDS, ROUTES } from '../../../util/vars';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import { WeeklyLeadersEmailsPageComponent } from '../WeeklyLeadersEmails/WeeklyLeadersEmailsPageComponent';
import { ImpactReportPageComponent } from '../ImpactReports/ImpactReportsPageComponent';
import { useModal } from '../../../hooks/useModal';
import { AddEditGoalModal } from './modules/AddEditGoalModal';
import { DeleteConfirmModal } from './modules/DeleteConfirmModal';
import { TabsNav } from '../../../components/tabs/Tabs';
import { FieldArray, Formik, useFormikContext } from 'formik';
import useYearGroups from '../../../hooks/useYearGroups';
import debounce from 'lodash/debounce';

export type SchoolGoal = {
  goal_key: string;
  cohort: {
    id: number | null;
    name: 'yearGroup' | 'group' | 'tag' | null;
  };
  metrics: (string | null)[];
  notes: string | null;
};

type SchoolImplementationPlanPostBody = {
  vision: string;
  goals: SchoolGoal[];
};

const AutoSubmitImplementationForm = () => {
  const { values, submitForm, isSubmitting } = useFormikContext<SchoolImplementationPlanPostBody>();
  const previousVisionRef = useRef(values.vision);
  const previousGoalsRef = useRef(JSON.stringify(values.goals));

  useEffect(() => {
    if (values.vision === previousVisionRef.current || isSubmitting) {
      return;
    }

    const autoSubmitFn = () => {
      previousVisionRef.current = values.vision;
      submitForm();
    };

    const debouncedAutoSubmit = debounce(autoSubmitFn, 600);
    debouncedAutoSubmit();

    return () => debouncedAutoSubmit.cancel();
  }, [ values.vision, submitForm, isSubmitting ]);

  useEffect(() => {
    const goalsSnapshot = JSON.stringify(values.goals);
    if (goalsSnapshot === previousGoalsRef.current || isSubmitting) {
      return;
    }

    const autoSubmitFn = () => {
      previousGoalsRef.current = goalsSnapshot;
      submitForm();
    };

    const debouncedAutoSubmit = debounce(autoSubmitFn, 600);
    debouncedAutoSubmit();

    return () => debouncedAutoSubmit.cancel();
  }, [ values.goals, submitForm, isSubmitting ]);

  return null;
};

const ImplementationPageComponent = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { translate: t } = useI18n();
  const { schoolId } = useParams<{ schoolId: string }>();

  const backToSchools = () => history.push(`${ROUTES.superAdmin}${ADMIN_ROUTES.schools}`);

  const getWeeklyLeadersEmails = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_WEEKLY_LEADERS_EMAILS, schoolId ],
    queryFn: () => repository.AdminRepository.getSchoolWeeklyLeadersEmail(Number(schoolId)),
  });

  const weeklyLeadersEmail = getWeeklyLeadersEmails.data?.data ?? null;

  if (!weeklyLeadersEmail) {
    return null;
  }

  return (
    <AdminDashboardLayout
      title={t('pages.ADMIN.HEADER')}
      action={
        <Button
          type={'button'}
          isOutline
          onClick={() => backToSchools()}
        >
          <MaterialIcon
            icon={'ChevronLeft'}
          />
          Change school
        </Button>}
    >
      <div className={'flex gap-5'}>
        <div className={'flex flex-col gap-5 w-1/4 p-md rounded-lg bg-white shadow'}>
          <h2 className={'text-2xl font-bold'}>Implementation Plan</h2>
          <span className={'text-xl font-bold'}>
            {weeklyLeadersEmail.school.name}, {weeklyLeadersEmail.school.postcode}, ID: {weeklyLeadersEmail.school.id}
          </span>
          <hr className={'h-px my-4 bg-gray-300 border-0'} />
          <Implementation />
        </div>
        <div className={'flex flex-col gap-5 w-3/4'}>
          <TabsNav
            id={'implementationTabs'}
            tabs={[
              {
                to: `${url}${ADMIN_ROUTES.implementation.weeklyLeadersEmails}`,
                label: t('pages.ADMIN.LEADERS_EMAILS.TITLE'),
              },
              {
                to: `${url}${ADMIN_ROUTES.implementation.impactReports}`,
                label: t('pages.ADMIN.IMPACT_REPORTS.TITLE'),
              },
            ]}
          />
          <ImplemetationRoutes />
        </div>
      </div>
    </AdminDashboardLayout>
  );
};

const ImplemetationRoutes = () => {
  const { path } = useRouteMatch();
  return (
    <Switch>
      <Route path={`${path}${ADMIN_ROUTES.implementation.impactReports}/:jobId?`}>
        <ImpactReportPageComponent />
      </Route>
      <Route path={`${path}${ADMIN_ROUTES.implementation.weeklyLeadersEmails}`}>
        <WeeklyLeadersEmailsPageComponent />
      </Route>
    </Switch>
  );
};

const Implementation = () => {
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();
  const { schoolId } = useParams<{ schoolId: string }>();
  const formikRef = useRef(null);

  const getSchoolImplementationPlan = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPLEMENTATION_PLAN, schoolId ],
    queryFn: () => repository.SchoolImplementationPlanRepository.get(Number(schoolId)),
  });

  const defaultGoals = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_GOALS ],
    queryFn: () => repository.SchoolImplementationPlanRepository.defaultGoals(Number(schoolId)),
  });

  const [ yearGroups ] = useYearGroups(Number(schoolId));

  const classesQuery = useQuery(
    [ QUERIES_IDS.ADMIN.GET_SCHOOL_CLASSES, schoolId ],
    () => repository.AdminRepository.getSchoolClasses(Number(schoolId)),
  );

  const tagsQuery = useQuery(
    [ QUERIES_IDS.ADMIN.GET_SCHOOL_TAGS, schoolId ],
    () => repository.AdminRepository.getSchoolTags(Number(schoolId)),
  );

  const [ addEditModal, toggleAddEditModal, setAddEditModalProps ] = useModal((props: GoalModalProps) => (
    <AddEditGoalModal {...props} />
  ));

  const [ confirmModal, toggleConfirmModal, setConfirmModalProps ] = useModal((props: GoalModalProps) => (
    <DeleteConfirmModal {...props} />
  ));

  const onSuccess = () => { console.log('Success'); };
  const onError = () => { console.log('Error'); };

  const mutations = {
    save: useMutation(
      (formData: SchoolImplementationPlanPostBody) => repository.SchoolImplementationPlanRepository.save(Number(schoolId), formData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOLS ], exact: false });
          queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPLEMENTATION_PLAN ], exact: false });
          onSuccess?.();
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          onError?.();
        },
      },
    ),
  };

  const handleAdd = (addGoal: (newGoal: GoalProps) => void) => () => {
    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NEW_GOAL'),
      data: null,
      defaultGoals: defaultGoals.data ?? [],
      yearGroups: yearGroups.data ?? [],
      schoolClasses: classesQuery.data?.data ?? [],
      schoolTags: tagsQuery.data?.data ?? [],
      disableMaskClick: true,
      onSuccess: addGoal,
      onError,
    });

    toggleAddEditModal(true);
  };

  const handleEdit = (editGoal: (goal: GoalProps) => void, removeGoal: (goal: GoalProps) => void) => (data: GoalProps) => {
    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.EDIT_GOAL'),
      mode: 'edit',
      toggleConfirm: toggleConfirmModal,
      data: data,
      defaultGoals: defaultGoals.data ?? [],
      yearGroups: yearGroups.data ?? [],
      schoolClasses: classesQuery.data?.data ?? [],
      schoolTags: tagsQuery.data?.data ?? [],
      disableMaskClick: true,
      onSuccess: editGoal,
      onError,
    });

    setConfirmModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.DELETE_GOAL'),
      toggle: toggleAddEditModal,
      toggleConfirm: toggleConfirmModal,
      data: data,
      yearGroups: [],
      disableMaskClick: true,
      onSuccess: removeGoal,
      onError,
    });

    toggleAddEditModal(true);
  };

  if (getSchoolImplementationPlan.isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {addEditModal}
      {confirmModal}
      <Formik
        innerRef={formikRef}
        initialValues={{
          vision: getSchoolImplementationPlan.data?.vision || '',
          goals: getSchoolImplementationPlan.data?.goals || [],
        }}
        onSubmit={(values, formikHelpers) => {
          mutations.save.mutate(values, {
            onSettled: () => {
              formikHelpers.setSubmitting(false);
            },
          });
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <form onSubmit={handleSubmit} className={'space-y-4'}>
            <div className={'space-y-2'}>
              <h4 className={'text-lg font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.VISION')}</h4>
              <Textarea
                id={'vision'}
                label={''}
                value={values.vision}
                onChange={(e) => setFieldValue('vision', e.target.value)}
              />
              <AutoSubmitImplementationForm />
            </div>
            <div className={'space-y-2'}>
              <FieldArray name={'goals'}>
                {({ push, replace, remove }) => (
                  <>
                    <div className={'flex justify-between items-center'}>
                      <h4 className={'text-lg font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.GOALS')}</h4>
                      <ButtonIcon
                        type={'button'}
                        icon={'Add'}
                        asLink
                        size={'md'}
                        color={'positive'}
                        onClick={handleAdd((newGoal: GoalProps) => push(newGoal))}
                        disabled={values.goals.length >= 3}
                      />
                    </div>
                    {values.goals?.length ? (
                      values.goals.map((goal, index) => (
                        <GoalCard
                          key={goal.goal_key}
                          goal={goal}
                          handleEdit={handleEdit((editedGoal: GoalProps) => replace(index, editedGoal), () => remove(index))}
                        />
                      ))
                    ) : (
                      <p className={'text-gray-500'}>No goals set yet.</p>
                    )}
                  </>)}
              </FieldArray>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

const GoalCard = ({ goal, handleEdit }: { goal: GoalProps, handleEdit: (data: GoalProps) => void }) => {
  const { translate: t } = useI18n();
  return (
    <div className={'flex flex-col gap-2 p-4 bg-white rounded-lg shadow'}>
      <div className={'flex justify-between items-center'}>
        <h3 className={'text-lg font-semibold'}>{t(`pages.ADMIN.IMPACT_REPORTS.GOALS.${goal.goal_key}`)}</h3>
        <ButtonIcon
          type={'button'}
          icon={'Edit'}
          asLink
          size={'sm'}
          onClick={() => handleEdit(goal)}
        />
      </div>
      {goal.metrics.length > 0 && (
        <ol className={'mt-2 pl-5 space-y-2 list-disc'}>
          {goal.metrics.map((metric: GoalProps['metrics'][number], index: number) => (
            <li key={index}>{t(`pages.ADMIN.IMPACT_REPORTS.METRICS.${metric}`)}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

export { ImplementationPageComponent };

